import { AggregateRowModel, ColumnModel } from '@syncfusion/ej2-angular-grids';
import { HandlingUnitDetailDTO } from '../../models/handling-unit-detail';
import { packageBreakdown } from '../../models/package-breakdown';
import { KW_QUANTITY_FORMAT } from '../../../../../@core/utils/ejs-constants';


export interface LotUnitValue {
  label: string;
  value: number;
}

/** One summary row: its unit-of-measure and component quantities live under `unit_` fields so each gets its own column. */
export interface LotUnitRow {
  key: string;
  qty: number;
  totalWeight: number;
  units: LotUnitValue[];
  [field: string]: unknown;
}

export interface LotUnitSummary {
  columns: ColumnModel[];
  aggregate: AggregateRowModel;
  totals: LotUnitValue[];
  totalWeight: number;
  hasWeight: boolean;
}

const unitField = (label: string) => `unit_${label}`;

/** Totals one group of a lot's handling unit details; package fields come from its first detail, as before. */
export function toUnitRow(key: string, huds: HandlingUnitDetailDTO[]): LotUnitRow {
  const first = huds[0] ?? {};
  const qty = huds.reduce((sum, hud) => sum + (hud.quantity ?? 0), 0);
  const row: LotUnitRow = { key, qty, totalWeight: qty * (first.packageWeight ?? 0), units: [] };

  // With components the partial package is shown as components (14 Box + 2 Bag), otherwise as is (14.5 Box).
  const hasComponents = !!(first.packageHasComponents && first.packageComponentName);
  const { packages, components } = packageBreakdown(qty, first.packageComponentQuantityPerPackage);

  if (first.packageUnitOfMeasure) {
    const value = hasComponents ? packages : qty;
    row[unitField(first.packageUnitOfMeasure)] = value;
    row.units.push({ label: first.packageUnitOfMeasure, value });
  }

  if (hasComponents) {
    row[unitField(first.packageComponentName)] = components;
    row.units.push({ label: first.packageComponentName, value: components });
  }

  return row;
}

/** One column (and footer sum) per unit label found on the rows, then Weight when the lot has any. */
export function summarizeUnits(rows: LotUnitRow[]): LotUnitSummary {
  const labels = [...new Set(rows.flatMap(row => row.units.map(unit => unit.label)))];
  const totals = labels.map(label => ({
    label,
    value: rows.reduce((sum, row) => sum + ((row[unitField(label)] as number) ?? 0), 0)
  }));
  const totalWeight = rows.reduce((sum, row) => sum + row.totalWeight, 0);
  const hasWeight = totalWeight > 0;

  const columns: ColumnModel[] = labels.map(label => ({
    field: unitField(label), headerText: label, format: KW_QUANTITY_FORMAT, textAlign: 'Right', width: 120
  }));
  const aggregate: AggregateRowModel = {
    columns: labels.map(label => ({ field: unitField(label), type: 'Sum', format: KW_QUANTITY_FORMAT }))
  };

  if (hasWeight) {
    columns.push({ field: 'totalWeight', headerText: 'Weight', format: KW_QUANTITY_FORMAT, textAlign: 'Right', width: 120 });
    aggregate.columns.push({ field: 'totalWeight', type: 'Sum', format: KW_QUANTITY_FORMAT });
  }

  return { columns, aggregate, totals, totalWeight, hasWeight };
}

/** Groups items by `keyOf` and totals each group's details into a summary row. */
export function groupUnitRows<T>(items: T[], keyOf: (item: T) => string, detailsOf: (item: T) => HandlingUnitDetailDTO[]): LotUnitRow[] {
  const groups = new Map<string, HandlingUnitDetailDTO[]>();
  for (const item of items) {
    const key = keyOf(item);
    groups.set(key, [...(groups.get(key) ?? []), ...detailsOf(item)]);
  }
  return [...groups].map(([key, huds]) => toUnitRow(key, huds));
}
